/**
 * Which services a customer is shown after "Termin buchen" — the preview of
 * the rule the agent applies (backend: app/utils/service_audience.py
 * `overview_services`, and `_service_overview_text` in static_flow_handler.py).
 * Keep the two in step; the tests use the same cases.
 */
import type { ServiceAudience, ServiceGroup } from '../../services/authService';

export type Viewer = 'female' | 'male' | 'unknown';

export interface OverviewGroup {
  name: string;
  services: string[];
}

export interface Overview {
  groups: OverviewGroup[];
  /** true when only the groups for the viewer's gender are shown. */
  narrowed: boolean;
  /**
   * What the message looks like: the full list, the group names only (the
   * list does not fit one WhatsApp message), or just the question (not even
   * that helps — one oversized group).
   */
  mode: 'list' | 'groups' | 'question';
}

/** WhatsApp takes this many characters per message. */
const WHATSAPP_TEXT_LIMIT = 4096;
// The real greeting carries the customer's first name; a long one keeps the
// preview on the safe side of the limit.
const GREETING = 'Gerne, Maximilian';
const AUDIENCE_LABEL: Record<Viewer, string> = { female: ' (Damen)', male: ' (Herren)', unknown: '' };

export const audienceFromFlags = (women: boolean, men: boolean): ServiceAudience => {
  if (women && men) return 'both';
  if (women) return 'female';
  if (men) return 'male';
  return 'none';
};

export const isFor = (audience: ServiceAudience, viewer: 'female' | 'male'): boolean =>
  audience === viewer || audience === 'both';

/** A group's services as the list shows them: by name, each once. */
const listed = (group: ServiceGroup, standaloneOnly: boolean): OverviewGroup => ({
  name: group.name,
  services: Array.from(
    new Set(group.services.filter((s) => !standaloneOnly || s.standalone).map((s) => s.name)),
  ),
});

const fits = (lines: string[]): boolean => lines.join('\n').length <= WHATSAPP_TEXT_LIMIT;

export function overviewFor(groups: ServiceGroup[], viewer: Viewer, siteName = ''): Overview {
  // Add-ons are refused without a main service, so they are never offered here.
  const anyStandalone = groups.some((g) => g.services.some((s) => s.standalone));
  const shownFrom = (source: ServiceGroup[], standaloneOnly: boolean) =>
    source.map((g) => listed(g, standaloneOnly)).filter((g) => g.services.length > 0);

  const mine =
    viewer === 'unknown' ? [] : shownFrom(groups.filter((g) => isFor(g.audience, viewer)), true);
  const narrowed = mine.length > 0;
  const shown = narrowed ? mine : shownFrom(groups, anyStandalone);
  const label = narrowed ? AUDIENCE_LABEL[viewer] : '';

  const list = [
    `${GREETING} – hier sind die verfügbaren Dienstleistungen${label} bei *${siteName}*:`,
    ...shown.flatMap((g) => [
      '',
      ...(shown.length > 1 ? [`*${g.name}*`] : []),
      ...g.services.map((name) => `• ${name}`),
    ]),
    '',
    'Welche dieser Leistungen möchten Sie gerne buchen?',
  ];
  if (fits(list)) return { groups: shown, narrowed, mode: 'list' };

  const areas = [
    `${GREETING} – bei *${siteName}* gibt es Leistungen${label} in diesen Bereichen:`,
    '',
    ...shown.map((g) => `• ${g.name}`),
    '',
    'Was möchten Sie gerne buchen?',
  ];
  return { groups: shown, narrowed, mode: shown.length > 1 && fits(areas) ? 'groups' : 'question' };
}

/** A group's services for the settings list: each name once, an extra only if every entry of that name is one. */
export function distinctServices(group: ServiceGroup): { name: string; standalone: boolean }[] {
  const byName = new Map<string, boolean>();
  for (const service of group.services) {
    byName.set(service.name, (byName.get(service.name) ?? false) || service.standalone);
  }
  return Array.from(byName, ([name, standalone]) => ({ name, standalone }));
}
